<div class="ai-settings">
    <!-- Header -->
    <div class="ai-settings__header">
        <div>
            <h1 class="ai-settings__title">{{ 'ai-admin.settings.title' | translate }}</h1>
            <p class="ai-settings__subtitle">
                {{ 'ai-admin.settings.subtitle' | translate }}
            </p>
        </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="ai-settings__loading">
        <p>{{ 'ai-admin.settings.loading' | translate }}</p>
    </div>

    <!-- Error State -->
    <div
        *ngIf="!loading && error"
        class="ai-settings__alert ai-settings__alert--error"
    >
        <div class="ai-settings__alert-icon">⚠️</div>
        <div class="ai-settings__alert-message">{{ error }}</div>
    </div>

    <!-- Success State -->
    <div
        *ngIf="successMessage"
        class="ai-settings__alert ai-settings__alert--success"
    >
        <div class="ai-settings__alert-icon">✅</div>
        <div class="ai-settings__alert-message">{{ successMessage }}</div>
    </div>

    <!-- Content -->
    <div *ngIf="!loading && settings" class="ai-settings__content">
        <form [formGroup]="form">
            <!-- Role-based Limits -->
            <div class="ai-settings__section">
                <div class="ai-settings__section-header">
                    <h2 class="ai-settings__section-title">{{ 'ai-admin.settings.limits.title' | translate }}</h2>
                    <p class="ai-settings__section-description">
                        {{ 'ai-admin.settings.limits.description' | translate }}
                    </p>
                </div>

                <div
                    *ngFor="let roleKey of getRoles()"
                    class="ai-settings__role-block"
                    [formGroup]="getLimitGroup(roleKey)"
                >
                    <!-- Role Header -->
                    <div class="ai-settings__role-header">
                        <div class="ai-settings__role-info">
                            <h3 class="ai-settings__role-name">
                                {{ getRoleLabel(roleKey) }}
                            </h3>
                            <p class="ai-settings__role-description">
                                {{ getRoleDescription(roleKey) }}
                            </p>
                        </div>
                    </div>

                    <!-- Limits Inputs -->
                    <div class="ai-settings__limits-grid">
                        <!-- Daily Limit -->
                        <div class="ai-settings__form-group">
                            <label class="ai-settings__label">
                                {{ 'ai-admin.settings.limits.daily.label' | translate }}
                                <span class="ai-settings__label-hint"
                                    >{{ 'ai-admin.settings.limits.daily.unit' | translate }}</span
                                >
                            </label>
                            <input
                                type="number"
                                formControlName="dailyTokenLimit"
                                class="ai-settings__input"
                                [placeholder]="'ai-admin.settings.limits.daily.placeholder' | translate"
                                min="1"
                            />
                            <p class="ai-settings__help-text">
                                {{ 'ai-admin.settings.limits.daily.hint' | translate }}
                            </p>
                        </div>

                        <!-- Weekly Limit -->
                        <div class="ai-settings__form-group">
                            <label class="ai-settings__label">
                                {{ 'ai-admin.settings.limits.weekly.label' | translate }}
                                <span class="ai-settings__label-hint"
                                    >{{ 'ai-admin.settings.limits.weekly.unit' | translate }}</span
                                >
                            </label>
                            <input
                                type="number"
                                formControlName="weeklyTokenLimit"
                                class="ai-settings__input"
                                [placeholder]="'ai-admin.settings.limits.weekly.placeholder' | translate"
                                min="1"
                            />
                            <p class="ai-settings__help-text">
                                {{ 'ai-admin.settings.limits.weekly.hint' | translate }}
                            </p>
                        </div>

                        <!-- Monthly Limit -->
                        <div class="ai-settings__form-group">
                            <label class="ai-settings__label">
                                {{ 'ai-admin.settings.limits.monthly.label' | translate }}
                                <span class="ai-settings__label-hint"
                                    >{{ 'ai-admin.settings.limits.monthly.unit' | translate }}</span
                                >
                            </label>
                            <input
                                type="number"
                                formControlName="monthlyTokenLimit"
                                class="ai-settings__input"
                                [placeholder]="'ai-admin.settings.limits.monthly.placeholder' | translate"
                                min="1"
                            />
                            <p class="ai-settings__help-text">
                                {{ 'ai-admin.settings.limits.monthly.hint' | translate }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Save Button -->
            <div class="ai-settings__actions">
                <button
                    type="submit"
                    (click)="save()"
                    [disabled]="saving || form.invalid"
                    class="ai-settings__btn ai-settings__btn--primary"
                >
                    <span *ngIf="!saving">{{ 'ai-admin.settings.save' | translate }}</span>
                    <span *ngIf="saving">{{ 'ai-admin.settings.saving' | translate }}</span>
                </button>
            </div>

            <!-- Info Note -->
            <div class="ai-settings__info-box">
                <p>{{ 'ai-admin.settings.note' | translate }}</p>
            </div>
        </form>

        <!-- Token Explanation -->
        <div class="ai-settings__section">
            <h2 class="ai-settings__section-title">
                {{ 'ai-admin.tokens.section-title' | translate }}
            </h2>
            <div class="ai-settings__explanation">
                <div class="ai-settings__explanation-item">
                    <h3 class="ai-settings__explanation-title">
                        {{ 'ai-admin.tokens.what-is.title' | translate }}
                    </h3>
                    <p class="ai-settings__explanation-text">
                        {{ 'ai-admin.tokens.what-is.description' | translate }}
                    </p>
                    <ul class="ai-settings__explanation-list">
                        <li>{{ 'ai-admin.tokens.what-is.approx-1' | translate }}</li>
                        <li>{{ 'ai-admin.tokens.what-is.approx-2' | translate }}</li>
                        <li>{{ 'ai-admin.tokens.what-is.approx-3' | translate }}</li>
                    </ul>
                </div>
                <div class="ai-settings__explanation-item">
                    <h3 class="ai-settings__explanation-title">
                        {{ 'ai-admin.tokens.example.title' | translate }}
                    </h3>
                    <div class="ai-settings__code-example">
                        <div class="ai-settings__code-line">
                            <span class="ai-settings__code-label"
                                >{{ 'ai-admin.tokens.example.user-question' | translate }}</span
                            >
                            <span class="ai-settings__code-content"
                                >"¿Cuáles son los mejores cursos de
                                Python?"</span
                            >
                        </div>
                        <div class="ai-settings__code-line">
                            <span class="ai-settings__code-label"
                                >{{ 'ai-admin.tokens.example.input-tokens' | translate }}</span
                            >
                            <span class="ai-settings__code-content"
                                >~15 tokens</span
                            >
                        </div>
                        <div class="ai-settings__code-line">
                            <span class="ai-settings__code-label"
                                >{{ 'ai-admin.tokens.example.ai-response' | translate }}</span
                            >
                            <span class="ai-settings__code-content"
                                >~200 tokens (párrafo con 3-4 cursos
                                recomendados)</span
                            >
                        </div>
                        <div
                            class="ai-settings__code-line ai-settings__code-line--total"
                        >
                            <span class="ai-settings__code-label">{{ 'ai-admin.tokens.example.total' | translate }}</span>
                            <span class="ai-settings__code-content"
                                >~215 tokens</span
                            >
                        </div>
                    </div>
                </div>
                <div class="ai-settings__explanation-item">
                    <h3 class="ai-settings__explanation-title">
                        {{ 'ai-admin.tokens.tips.title' | translate }}
                    </h3>
                    <ul class="ai-settings__explanation-list">
                        <li>{{ 'ai-admin.tokens.tips.tip-1' | translate }}</li>
                        <li>{{ 'ai-admin.tokens.tips.tip-2' | translate }}</li>
                        <li>{{ 'ai-admin.tokens.tips.tip-3' | translate }}</li>
                        <li>{{ 'ai-admin.tokens.tips.tip-4' | translate }}</li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</div>
